import type { TemplateFormLanguage } from '../types/templateForms';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from './constants';

export interface FormHeaderTranslations {
    submitted: {
        thankYou: string;
        draftSubmitted: string;
        sessionEnded: string;
        submissionSaved: string;
    };
    descriptions: {
        draftSubmitted: string;
        sessionEndedRefresh: string;
        submissionSavedRefresh: string;
        intakeCompletedFollowUp: string;
    };
    refreshLink: string;
    copyLinkTooltip: string;
    session: {
        timeoutWarningTitle: string;
        timeoutWarningMessage: (seconds: number) => string;
    };
}

const ENGLISH_TRANSLATIONS: FormHeaderTranslations = {
    submitted: {
        thankYou: "Thank you! We've received your form.",
        draftSubmitted: 'Draft form has been submitted.',
        sessionEnded: 'Your session ended due to inactivity.',
        submissionSaved: 'Your submission was saved due to inactivity.',
    },
    descriptions: {
        draftSubmitted:
            'We sent the draft form to your email and you can return anytime. Please note the law firm will not have access to your information until you fully submit the form.',
        sessionEndedRefresh:
            'Refresh the page to continue editing. Unsaved changes were not submitted.',
        submissionSavedRefresh:
            'Your progress was saved as a draft. Refresh the page to continue where you left off.',
        intakeCompletedFollowUp:
            'An email with updated intake form link has already been sent. You can access it to make any updates to your intake form.',
    },
    refreshLink: 'Click here to refresh and continue',
    copyLinkTooltip: 'Copy link',
    session: {
        timeoutWarningTitle: 'Session Timeout Warning',
        timeoutWarningMessage: (seconds) =>
            `You will be logged out in ${seconds} seconds due to inactivity.`,
    },
};

const FRENCH_TRANSLATIONS: FormHeaderTranslations = {
    submitted: {
        thankYou: 'Merci! Nous avons reçu votre formulaire.',
        draftSubmitted: 'Le brouillon du formulaire a été soumis.',
        sessionEnded: 'Votre session a pris fin en raison d’inactivité.',
        submissionSaved: 'Votre soumission a été enregistrée en raison d’inactivité.',
    },
    descriptions: {
        draftSubmitted:
            'Nous avons envoyé le brouillon du formulaire à votre courriel; vous pouvez y revenir en tout temps. Veuillez noter que le cabinet n’aura pas accès à vos renseignements tant que vous n’aurez pas soumis le formulaire au complet.',
        sessionEndedRefresh:
            'Actualisez la page pour continuer la modification. Les modifications non enregistrées n’ont pas été soumises.',
        submissionSavedRefresh:
            'Votre progression a été enregistrée comme brouillon. Actualisez la page pour reprendre où vous en étiez.',
        intakeCompletedFollowUp:
            'Un courriel contenant un lien mis à jour vers le formulaire d’admission a déjà été envoyé. Vous pouvez y accéder pour apporter des modifications à votre formulaire.',
    },
    refreshLink: 'Cliquez ici pour actualiser et continuer',
    copyLinkTooltip: 'Copier le lien',
    session: {
        timeoutWarningTitle: 'Avertissement d’expiration de session',
        timeoutWarningMessage: (seconds) =>
            `Votre session prendra fin dans ${seconds} secondes en raison d’inactivité.`,
    },
};

export const FORM_HEADER_TRANSLATIONS: Record<TemplateFormLanguage, FormHeaderTranslations> = {
    en: ENGLISH_TRANSLATIONS,
    fr: FRENCH_TRANSLATIONS,
};

export function getFormHeaderTranslations(
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): FormHeaderTranslations {
    return FORM_HEADER_TRANSLATIONS[language] ?? FORM_HEADER_TRANSLATIONS.en;
}
